import { useState, useCallback, useMemo } from "react";
import { ChangelogTweetDialog } from "./changelog-tweet-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import type { ChangelogEntryListItem } from "@/types/changelog";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import {
  getChangelogColumns,
} from "./changelog-columns";
import type { ChangelogAction } from "./changelog-actions";
import { ArticlePagination } from "../articles/article-pagination";
import {
  changelogEntryDetailQueryOptions,
  usePublishChangelogEntry,
  useUnpublishChangelogEntry,
  useDeleteChangelogEntry,
} from "@/hooks/use-changelog";
import { validateBilingualContent } from "@/lib/validation/changelog-schema";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface ChangelogTableProps {
  data: ChangelogEntryListItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
}

export function ChangelogTable({
  data,
  meta,
  pagination,
}: ChangelogTableProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tweetEntryId, setTweetEntryId] = useState<string | null>(null);
  const publishMutation = usePublishChangelogEntry();
  const unpublishMutation = useUnpublishChangelogEntry();
  const deleteMutation = useDeleteChangelogEntry();

  const handleAction = useCallback(
    async (entry: ChangelogEntryListItem, action: ChangelogAction) => {
      if (action === "edit") {
        navigate(`/changelog/${entry._id}/edit`);
        return;
      }

      if (action === "tweet") {
        setTweetEntryId(entry._id);
        return;
      }

      if (action === "publish") {
        try {
          const detail = await queryClient.fetchQuery(
            changelogEntryDetailQueryOptions(entry._id),
          );
          const { valid, missingFields } = validateBilingualContent({
            titleKo: detail.title.ko,
            titleEn: detail.title.en,
            bodyKo: detail.body.ko,
            bodyEn: detail.body.en,
          });

          if (!valid) {
            toast.error("한국어와 영문을 모두 작성해야 발행할 수 있습니다", {
              description: `미작성 항목: ${missingFields.join(", ")}`,
            });
            return;
          }
        } catch {
          toast.error("발행 전 검증에 실패했습니다", {
            description: "다시 시도해주세요.",
          });
          return;
        }

        publishMutation.mutate(entry._id);
        return;
      }

      if (action === "unpublish") {
        unpublishMutation.mutate(entry._id);
        return;
      }

      if (action === "delete") {
        if (!confirm("이 체인지로그를 삭제하시겠습니까?")) return;
        deleteMutation.mutate(entry._id);
      }
    },
    [navigate, queryClient, publishMutation, unpublishMutation, deleteMutation],
  );

  const columns = useMemo(
    () => getChangelogColumns(handleAction),
    [handleAction],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">
          체인지로그가 없습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="transition-colors duration-75 hover:bg-muted/50"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ArticlePagination meta={meta} pagination={pagination} />

      {tweetEntryId && (
        <ChangelogTweetDialog
          open={!!tweetEntryId}
          onOpenChange={(open) => {
            if (!open) setTweetEntryId(null);
          }}
          entryId={tweetEntryId}
        />
      )}
    </div>
  );
}
